import {each, isEmpty, map} from 'lodash';
import {createSelector} from 'reselect';
import {State} from 'redux/reducer';
import {IPartner, Partners} from 'types/partners';
import {INav} from './helpers/FoldersHelper';
import {IDialogProps, layoutReducerKey} from './types';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {IStatesResponse} from 'types/StatesResponse';
import {IDiplomatFolder} from 'types/DiplomatFolder';
import {IDiplomatObject} from 'types/DiplomatObject';
import {ITransaction} from 'types/diplomat/transaction/Transaction';

const foldersSelector = (state: State) => state[layoutReducerKey].folders;
const folderByName = (name: string) =>
   createSelector(foldersSelector, (folders): IDiplomatFolder | undefined => {
      return folders.find((folder) => folder.name === name);
   });
const keysSelector = (state: State) => state[layoutReducerKey].keys;
const keysCountSelector = createSelector(keysSelector, (keys) => {
   const {
      openpgp: {publicKeys = [], secretKeys = []} = {},
      ssh: {clientKeys = []} = {},
      ssl: {serverCerts = [], clientCerts = []} = {},
   } = keys;
   return {
      openpgp: publicKeys.length + secretKeys.length,
      ssh: clientKeys.length,
      ssl: serverCerts.length + clientCerts.length,
   };
});
const keysOpenPgpSecretKeysSelector = (state: State) =>
   state[layoutReducerKey].keys.openpgp?.secretKeys || [];
const keysOpenPgpPublicKeysSelector = (state: State) =>
   state[layoutReducerKey].keys.openpgp?.publicKeys || [];
const serverSslKeys = createSelector(keysSelector, (keys) => {
   return keys?.ssl?.serverCerts || [];
});
const sslCertRootFolder = (client?: boolean) =>
   createSelector(foldersSelector, (folders) => {
      const objectType = client
         ? DiplomatObjectType.enum.SSL_CLIENT_CERTIFICATE
         : DiplomatObjectType.enum.SSL_CERTIFICATE;
      const level = client ? 3 : 2;
      const folder = folders.find(
         (folder) =>
            folder.addable && folder.childObjectType === objectType && folder.level === level
      );
      return folder?.id || null;
   });

const vfsGroupsSelector = (state: State) => state[layoutReducerKey].vfsGroups;

const transactionSyncFoldersSelector = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.filter(
         (f) =>
            DiplomatObjectType.enum.INBOUND_TRANSACTION === f.childObjectType ||
            f.childObjectType === DiplomatObjectType.enum.OUTBOUND_TRANSACTION ||
            f.childObjectType === DiplomatObjectType.enum.SYNCHRONIZATION
      )
);

const partnersFoldersSelector = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.filter(
         (f) =>
            f.childObjectType === DiplomatObjectType.enum.EXTERNAL_PARTNER ||
            f.childObjectType === DiplomatObjectType.enum.INTERNAL_PARTNER
      )
);

const inboundTransactionFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.find(
         (f) => f.level === 2 && DiplomatObjectType.enum.INBOUND_TRANSACTION === f.childObjectType
      )?.id
);
const outboundTransactionFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.find(
         (f) => f.level === 2 && DiplomatObjectType.enum.OUTBOUND_TRANSACTION === f.childObjectType
      )?.id
);

const publicPartnerFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.find(
         (f) => f.level === 2 && DiplomatObjectType.enum.EXTERNAL_PARTNER === f.childObjectType
      )?.id
);
const trustedPartnerFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.find(
         (f) => f.level === 2 && DiplomatObjectType.enum.INTERNAL_PARTNER === f.childObjectType
      )?.id
);

const synchronizationFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.find((f) => DiplomatObjectType.enum.SYNCHRONIZATION === f.childObjectType)?.id
);

const openPGPKeyFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders && folders.find((f) => DiplomatObjectType.enum.KEY_PAIR === f.childObjectType)?.id
);

const openPGPPublicKeyFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders && folders.find((f) => DiplomatObjectType.enum.PUBLIC_KEY === f.childObjectType)?.id
);

const SSHKeyPairFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders && folders.find((f) => DiplomatObjectType.enum.SSH_KEY === f.childObjectType)?.id
);

const SSHPulicKeyFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.find((f) => DiplomatObjectType.enum.SSH_PUBLIC_KEY === f.childObjectType)?.id
);
const SSLClientCertFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.find((f) => DiplomatObjectType.enum.SSL_CLIENT_CERTIFICATE === f.childObjectType)?.id
);
const SSLServerCertFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders &&
      folders.find((f) => DiplomatObjectType.enum.SSL_CERTIFICATE === f.childObjectType)?.id
);
const sftpUsersFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders && folders.find((f) => DiplomatObjectType.enum.SFTP_USER === f.childObjectType)?.id
);
const sftpGroupsFolderId = createSelector(
   foldersSelector,
   (folders: IDiplomatFolder[]) =>
      folders && folders.find((f) => DiplomatObjectType.enum.VFS_GROUP === f.childObjectType)?.id
);

const clientSslKeys = createSelector(keysSelector, (keys) => {
   return keys?.ssl?.clientCerts || [];
});

const clientSshKeys = createSelector(keysSelector, (keys) => {
   return keys?.ssh?.clientKeys || [];
});

const clientSshFilteredKeys = createSelector(clientSshKeys, (keys: IDiplomatObject[]) =>
   keys.filter((k) => k.objectType === DiplomatObjectType.enum.SSH_KEY)
);

const clientSshFilteredPublicKeys = createSelector(clientSshKeys, (keys: IDiplomatObject[]) =>
   keys.filter((k) => k.objectType === DiplomatObjectType.enum.SSH_PUBLIC_KEY)
);

const clientSshFilteredcKeyPairs = createSelector(clientSshKeys, (keys: IDiplomatObject[]) =>
   keys.filter((k) => k.objectType === DiplomatObjectType.enum.SSH_PUBLIC_KEY)
);

const hostSshKeys = createSelector(keysSelector, (keys) => keys?.ssh?.hostKeys || []);

const treeFilter = (state: State) => state[layoutReducerKey].treeFilter;
const statesSince = (state: State) => state[layoutReducerKey].statesSince;

const statesSelector = (state: State) => state[layoutReducerKey].states;
const objectsFromStates = createSelector(statesSelector, (states: IStatesResponse) => {
   // return map(states.statesMap, (s) => s.diplomatObject);
   const objects = [] as IDiplomatObject[];
   each(states.statesMap, (folder) => each(folder, (s) => objects.push(s.diplomatObject)));
   return objects;
});

const filteredObjectsFromStates = createSelector(treeFilter, objectsFromStates, (filter, items) => {
   if (!filter.search) return items;
   const search = filter.search.toLowerCase();
   return items.filter((o) => o.name.toLowerCase().indexOf(search) >= 0) || [];
});

const partnersSelector = (state: State) => state[layoutReducerKey].partners;
const transactionsSelector = (state: State) => state[layoutReducerKey].transactions;
const synchronizationsSelector = createSelector(objectsFromStates, (objects: IDiplomatObject[]) =>
   objects.filter((o) => o.objectType === DiplomatObjectType.enum.SYNCHRONIZATION)
);
const transactionsSyncsSelector = createSelector(
   transactionsSelector,
   synchronizationsSelector,
   (transactions, syncs) => {
      return [...transactions, ...syncs];
   }
);

const partnersCount = createSelector(
   partnersSelector,
   (partners: Partners) => partners.publicPartners?.length + partners.trustedPartners?.length
);
const partnersCounts = createSelector(partnersSelector, (partners: Partners) => [
   partners.publicPartners?.length,
   partners.trustedPartners?.length,
]);

const transactionsCount = createSelector(
   transactionsSelector,
   (transactions: ITransaction[]) => transactions.length
);

const transactionsCounts = createSelector(transactionsSelector, (transactions: ITransaction[]) => {
   const counts = [0, 0, 0];
   transactions?.forEach?.((t) => {
      if (t.objectType === DiplomatObjectType.enum.INBOUND_TRANSACTION) {
         counts[0]++;
      } else if (t.objectType === DiplomatObjectType.enum.OUTBOUND_TRANSACTION) {
         counts[1]++;
      } else {
         counts[2]++;
      }
   });
   return counts;
});

// const transactionRunnablesCount = createSelector(
//    transactionsSelector,
//    (transactions: ITransaction[]) => transactions.filter(t => (t.))
// );

const countItems = (states: IStatesResponse['statesMap']) => {
   let length = 0;
   each(states, (folder) => (length += Object.keys(folder).length));
   return length;
};
const getItem = (states: IStatesResponse['statesMap'], folderId: string, name: string) => {
   return states?.[folderId]?.[name];
};
const getStatus = (state: State, folderId: string, name: string) =>
   getItem(state[layoutReducerKey].states?.statesMap, folderId, name)?.state?.typeId;

const getEdgeGatewayStatuses = (state: State) => state[layoutReducerKey].states.edgeGateways;

const treeNavSelector = (state: State) => state[layoutReducerKey].treeNav;
// function flatThroughTree(result: INavState[], nodes: INav[], depth: number = 0) {
//    each(nodes, (node, i) => {
//       result.push({
//          ...node,
//          _state: {
//             depth,
//          },
//       });
//       node.child && flatThroughTree(result, node.child, depth + 1);
//    });
// }
// const flatenedTreeSelector = createSelector(treeNavSelector, (tree: INav[]) => {
//    const flattened = [] as INavState[];
//    flatThroughTree(flattened, tree, 0);
//    return flattened;
// });

const getFolders = (state: State) => state[layoutReducerKey].folders;

const treeStateSelector = (state: State) => state[layoutReducerKey].treeState;
const getPartners = partnersSelector;

const getPartnerList = createSelector(getPartners, (partners: Partners): IPartner[] => {
   return [...(partners.publicPartners || []), ...(partners.trustedPartners || [])]
      .filter((partner) => partner.name)
      .sort((a, b) => a.name.localeCompare(b.name));
});

export const prepareTreeNav = (treeNav: INav[]) => {
   // console.log(treeNav);
   if (isEmpty(treeNav)) {
      return [];
   }

   return treeNav;
};

const treeNav = createSelector(treeNavSelector, prepareTreeNav);
const treeNavCount = createSelector(treeNavSelector, (treeNav: INav[]) => treeNav?.[0]?.count);

const vfsNodes = createSelector(treeNavSelector, (tree) => {
   return tree[0]?.child
      ?.find((n) => n.childObjectType === DiplomatObjectType.enum.SFTP_SERVER)
      ?.child?.find((n) => n.childObjectType === DiplomatObjectType.enum.VFS);
});

const transactionNames = createSelector(
   transactionsSelector,
   synchronizationsSelector,
   (transactions, syncs) => {
      return map([...transactions, ...syncs], (transaction) => transaction.name).sort();
   }
);

const getDialogs = (state: State) => state[layoutReducerKey].dialogs;

const isDialogActive = (state: State, dialogId: string) =>
   !!state[layoutReducerKey].dialogs.find((dialog) => dialog.id === dialogId);

const isPermissionsOpened = (packedId: string) =>
   createSelector(
      getDialogs,
      (dialogs: IDialogProps[]) => !!dialogs.find((dialog) => dialog.data?.id === packedId)
   );

export default {
   isFetching: (state: State) => state[layoutReducerKey].isFetching,
   treeFilter,
   treeNav,
   treeNavCount,
   treeNavSelector,
   // flatenedTreeSelector,
   treeStateSelector,
   getFolders,
   getPartners,
   getPartnerList,
   partnersCount,
   partnersCounts,
   transactionsCount,
   transactionsCounts,
   // transactionRunnablesCount,
   transactionNames,
   keysSelector,
   keysCountSelector,
   folderByName,
   sslCertRootFolder,
   vfsGroupsSelector,
   vfsNodes,
   transactionSyncFoldersSelector,
   partnersFoldersSelector,
   inboundTransactionFolderId,
   outboundTransactionFolderId,
   synchronizationFolderId,
   publicPartnerFolderId,
   trustedPartnerFolderId,
   openPGPKeyFolderId,
   openPGPPublicKeyFolderId,
   SSHKeyPairFolderId,
   SSHPulicKeyFolderId,
   SSLClientCertFolderId,
   SSLServerCertFolderId,
   sftpUsersFolderId,
   sftpGroupsFolderId,
   serverSslKeys,
   clientSslKeys,
   clientSshKeys,
   clientSshFilteredKeys,
   clientSshFilteredPublicKeys,
   hostSshKeys,
   keysOpenPgpSecretKeysSelector,
   keysOpenPgpPublicKeysSelector,
   transactionsSelector,
   transactionsSyncsSelector,
   countItems,
   getItem,
   getStatus,
   statesSince,
   statesSelector,
   getEdgeGatewayStatuses,
   objectsFromStates,
   filteredObjectsFromStates,
   isDialogActive,
   isPermissionsOpened,
   partnersSelector,
   synchronizationsSelector,
   statesCount: createSelector(statesSelector, (states: IStatesResponse) =>
      states.statesMap ? Object.keys(states.statesMap).length : 0
   ),
   getCalendars: (state: State) => state[layoutReducerKey].calendars,
   getDialogs,

   admins: (state: State) => state[layoutReducerKey].admins,
   authPolicies: (state: State) => state[layoutReducerKey].authPolicies,
   passwordData: (state: State) => state[layoutReducerKey].passwordData,

   archiveUsed: (state: State) => state[layoutReducerKey].audit?.archiveUsed,
   archiveDateRange: (state: State) => state[layoutReducerKey].audit?.archiveDateRange,

   activeDialogStatus: (state: State) => state[layoutReducerKey].activeDialogStatus,

   isSupportTicketGenerating: (state: State) => state[layoutReducerKey].supportTicketGenerating,

   toggledPanels: (state: State) => state[layoutReducerKey].toggledPanels,

   jobMonitorColumnsOrder: (state: State) => state[layoutReducerKey].jobMonitorColumnsOrder,
};
